import { TabPanel, TabView } from "primereact/tabview";
import { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import Select from "react-select";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import {
  update_lease,
  get_lease,
  getCustomersURL,
  getUnitsForFacility,
  GET_CURRENCIES_URL,
  get_lease_templates,
  getBankDetailsURL,
  getGLAccountsURL,
  getDigitaxConfigURL
} from "../../../../utils/urls";

const EditLeaseDialog = ({ visible, onHide, leaseData, onUpdate, leaseId }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    // Basic Info
    unitId: "",
    unitName: "",
    tenantId: "",
    tenantName: "",
    landlordId: "",
    landlordName: "",
    currency: null,
    status: "Active",
    notes: "",

    // Lease Terms
    startDate: null,
    endDate: null,
    duration: "",
    autoRenewal: false,
    leaseTemplate: "",

    // Financial Terms
    monthlyRent: "",
    securityDeposit: "",
    paymentDueDate: "",
    balanceBroughtForward: "0",
    taxEnabled: true,
    digitaxEnabled: false,
    digitaxProfileId: null,
    digitaxSelection: "",

    // Payment Methods
    paymentMethods: [{ type: "Cash", details: { preferredCashLocation: "" }, isPrimary: true }],

    // Billing
    billingFrequency: "Monthly",
    nextInvoiceDate: null,
    autoSend: false,

    // GL Accounts
    glAccounts: {
      invoice: {
        debit: null,
        credit: null
      },
      payment: {
        debit: null,
        credit: null
      }
    }
  });

  // Dropdown options
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [bankDetails, setBankDetails] = useState([]);
  const [glAccountsList, setGlAccountsList] = useState([]);

  const [editReason, setEditReason] = useState("");
  const [digitaxProfiles, setDigitaxProfiles] = useState([]);

  // Load lease data and populate form
  useEffect(() => {
    if (visible && (leaseId || leaseData)) {
      console.log("EditLeaseDialog props:", { leaseId, leaseData: typeof leaseData === 'object' ? leaseData : 'not object' });
      fetchDigitaxProfiles();
      loadLeaseData();
      loadDropdownData();
    }
  }, [visible, leaseId, leaseData]);

  // Update tenant and landlord names after customers are loaded
  useEffect(() => {
    if (formData.tenantId && !formData.tenantName && tenants.length > 0) {
      const tenant = tenants.find(t => t._id === formData.tenantId);
      if (tenant) {
        setFormData(prev => ({
          ...prev,
          tenantName: tenant.fullName || `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim()
        }));
      }
    }

    if (formData.landlordId && !formData.landlordName && landlords.length > 0) {
      const landlord = landlords.find(l => l._id === formData.landlordId);
      if (landlord) {
        setFormData(prev => ({
          ...prev,
          landlordName: landlord.fullName || `${landlord.firstName || ''} ${landlord.lastName || ''}`.trim()
        }));
      }
    }
  }, [formData.tenantId, formData.landlordId, tenants, landlords]);

  const fetchDigitaxProfiles = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getDigitaxConfigURL}/${facilityId}`,
        "GET"
      );

      console.log("Digitax config response:", response);

      if (response.success) {
        setDigitaxProfiles(
          response.data?.digitax?.profiles || []
        );
      }
    } catch (error) {
      console.error(
        "Failed to fetch DigiTax profiles:",
        error
      );
    }
  };

  const loadLeaseData = async () => {
    try {
      setLoading(true);
      let lease = leaseData;

      if (!lease && leaseId) {
        const facilityId = await getItem("selectedFacilityId");
        const response = await makeRequest2(`${get_lease}/${facilityId}/${leaseId}`, "GET");
        if (response.success) {
          lease = response.data;
        }
      }

      if (lease) {
        console.log("Processing lease data:", lease);

        // Handle tenant information
        let tenantId = "";
        let tenantName = "";
        if (typeof lease.tenant === 'object' && lease.tenant) {
          tenantId = lease.tenant._id;
          tenantName = lease.tenant.fullName || `${lease.tenant.firstName || ''} ${lease.tenant.lastName || ''}`.trim();
        } else if (lease.tenant) {
          tenantId = lease.tenant;
        }

        // Handle landlord information
        let landlordId = "";
        let landlordName = "";
        if (typeof lease.landlord === 'object' && lease.landlord) {
          landlordId = lease.landlord._id;
          landlordName = lease.landlord.fullName || `${lease.landlord.firstName || ''} ${lease.landlord.lastName || ''}`.trim();
        } else if (lease.landlord) {
          landlordId = lease.landlord;
        }

        // ==========================================
        // DIGITAX
        // ==========================================

        const digitaxData = lease.financialTerms?.digitax;

        let digitaxEnabled = false;
        let digitaxProfileId = null;
        let digitaxSelection = "";

        if (digitaxData?.enabled === true) {
          digitaxEnabled = true;

          if (digitaxData.profileId) {
            // Existing profile-based DigiTax
            digitaxProfileId = digitaxData.profileId.toString();
            digitaxSelection = digitaxProfileId;
          } else {
            // Existing facility-level DigiTax
            digitaxProfileId = null;
            digitaxSelection = "legacy";
          }
        }

        setFormData({
          // Basic Info
          unitId: typeof lease.unitNumber === 'object' ? lease.unitNumber._id : lease.unitNumber,
          unitName: typeof lease.unitNumber === 'object' ? lease.unitNumber.name : '',
          tenantId: tenantId,
          tenantName: tenantName,
          landlordId: landlordId,
          landlordName: landlordName,
          currency: typeof lease.currency === 'object' ? {
            value: lease.currency._id,
            label: `${lease.currency.currencyName} (${lease.currency.currencyShortCode})`
          } : null,
          status: lease.status || "Active",
          notes: lease.notes || "",

          // Lease Terms
          startDate: lease.leaseTerms?.startDate ? new Date(lease.leaseTerms.startDate) : null,
          endDate: lease.leaseTerms?.endDate ? new Date(lease.leaseTerms.endDate) : null,
          duration: lease.leaseTerms?.duration?.toString() || "",
          autoRenewal: lease.leaseTerms?.autoRenewal || false,
          leaseTemplate: lease.leaseTemplate || "",

          // Financial Terms
          monthlyRent: lease.financialTerms?.monthlyRent?.toString() || "",
          securityDeposit: lease.financialTerms?.securityDeposit?.toString() || "",
          paymentDueDate: lease.financialTerms?.paymentDueDate?.toString() || "",
          balanceBroughtForward: lease.financialTerms?.balanceBroughtForward?.toString() || "0",
          taxEnabled: lease.financialTerms?.taxEnabled !== undefined ? lease.financialTerms.taxEnabled : true,
          digitaxEnabled: digitaxEnabled,
          digitaxProfileId: digitaxProfileId,
          digitaxSelection: digitaxSelection,

          // Payment Methods
          paymentMethods: lease.financialTerms?.paymentMethods?.length > 0
            ? lease.financialTerms.paymentMethods.map(method => ({
              type: method.type,
              details: method.details || {},
              bankDetailsId: method.bankDetailsId,
              isPrimary: method.isPrimary
            }))
            : [{ type: "Cash", details: { preferredCashLocation: "" }, isPrimary: true }],

          // Billing
          billingFrequency: lease.billingCycle?.frequency || "Monthly",
          nextInvoiceDate: lease.billingCycle?.nextInvoiceDate ?
            new Date(lease.billingCycle.nextInvoiceDate) : null,
          autoSend: lease.billingCycle?.autoSend || false,

          // GL Accounts
          glAccounts: lease.glAccounts || {
            invoice: {
              debit: null,
              credit: null
            },
            payment: {
              debit: null,
              credit: null
            }
          }
        });
      }
    } catch (error) {
      console.error('Error loading lease data:', error);
      toastify("Failed to load lease data", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadDropdownData = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");

      const [unitsRes, customersRes, currenciesRes, templatesRes, bankDetailsRes, glAccountsRes] =
        await Promise.all([
          makeRequest2(`${getUnitsForFacility}/${facilityId}`, "GET"),
          makeRequest2(`${getCustomersURL}/${facilityId}`, "GET"),
          makeRequest2(`${GET_CURRENCIES_URL}/${facilityId}`, "GET"),
          makeRequest2(`${get_lease_templates}/${facilityId}`, "GET"),
          makeRequest2(`${getBankDetailsURL}/${facilityId}`, "GET"),
          makeRequest2(`${getGLAccountsURL}/${facilityId}`, "GET")
        ]);

      if (unitsRes.success) {
        console.log("Units data:", unitsRes.data);
        setUnits(unitsRes.data);
      }

      if (customersRes.success) {
        console.log("Customers data:", customersRes.data);
        const allCustomers = customersRes.data;
        const tenantList = allCustomers.filter(c => c.customerType?.toLowerCase() === "tenant");
        const landlordList = allCustomers.filter(c => c.customerType?.toLowerCase() === "home owner");

        const dualRoleCustomers = allCustomers.filter(c =>
          c.customerType && (
            c.customerType.toLowerCase().includes("tenant") &&
            c.customerType.toLowerCase().includes("home owner")
          )
        );

        setTenants([...tenantList, ...dualRoleCustomers]);
        setLandlords([...landlordList, ...dualRoleCustomers]);
      }

      if (currenciesRes.success) {
        const currencyData = currenciesRes.data.data || currenciesRes.data;
        setCurrencies(Array.isArray(currencyData) ? currencyData : [currencyData]);
      }

      if (templatesRes.success) {
        setTemplates(templatesRes.data?.data || templatesRes.data || []);
      }

      if (bankDetailsRes.success) {
        const bankData = bankDetailsRes.data?.data || bankDetailsRes.data || [];
        setBankDetails(Array.isArray(bankData) ? bankData : []);
      }

      if (glAccountsRes.success) {
        let accountsData = [];
        if (Array.isArray(glAccountsRes.data)) {
          accountsData = glAccountsRes.data;
        } else if (glAccountsRes.data && glAccountsRes.data.data) {
          accountsData = glAccountsRes.data.data;
        } else if (glAccountsRes.data && glAccountsRes.data.accounts) {
          accountsData = glAccountsRes.data.accounts;
        }

        let finalAccounts = [];
        if (Array.isArray(accountsData)) {
          finalAccounts = accountsData.filter(account => account.isFinal === true);
          if (finalAccounts.length === 0) {
            finalAccounts = accountsData;
          }
        } else {
          finalAccounts = accountsData;
        }

        setGlAccountsList(finalAccounts);
      }

    } catch (error) {
      console.error('Error loading dropdown data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!editReason.trim()) {
      toastify("Please provide a reason for editing this lease", "error");
      return;
    }

    const actualLeaseId = leaseId || (typeof leaseData === 'object' && leaseData?._id);
    if (!actualLeaseId) {
      toastify("Lease ID is missing. Cannot update lease.", "error");
      return;
    }

    try {
      setSubmitting(true);
      const facilityId = await getItem("selectedFacilityId");

      let currencyId = null;
      if (formData.currency) {
        if (typeof formData.currency === 'object' && formData.currency.value) {
          currencyId = formData.currency.value;
        } else if (typeof formData.currency === 'string') {
          currencyId = formData.currency;
        }
      }

      const updateData = {
        unitNumber: formData.unitId,
        tenant: formData.tenantId,
        landlord: formData.landlordId,
        currency: currencyId,
        status: formData.status,
        notes: formData.notes,

        leaseTerms: {
          startDate: formData.startDate,
          endDate: formData.endDate,
          duration: parseInt(formData.duration) || 0,
          autoRenewal: formData.autoRenewal
        },

        leaseTemplate: formData.leaseTemplate,

        financialTerms: {
          monthlyRent: parseFloat(formData.monthlyRent) || 0,
          paymentDueDate: parseInt(formData.paymentDueDate) || 1,
          securityDeposit: parseFloat(formData.securityDeposit) || 0,
          balanceBroughtForward: parseFloat(formData.balanceBroughtForward) || 0,
          taxEnabled: formData.taxEnabled !== undefined ? Boolean(formData.taxEnabled) : true,
          digitax: {
            enabled: formData.digitaxSelection !== "",
            profileId:
              formData.digitaxSelection !== "" &&
                formData.digitaxSelection !== "legacy"
                ? formData.digitaxSelection
                : null
          },
          paymentMethods: formData.paymentMethods.map(method => {
            const processedMethod = {
              type: method.type,
              details: method.details || {},
              isPrimary: method.isPrimary
            };
            if (method.bankDetailsId && method.bankDetailsId !== "") {
              processedMethod.bankDetailsId = method.bankDetailsId;
            }
            return processedMethod;
          })
        },

        billingCycle: {
          frequency: formData.billingFrequency,
          nextInvoiceDate: formData.nextInvoiceDate,
          autoSend: formData.autoSend
        },

        glAccounts: formData.glAccounts,

        editHistory: {
          reason: editReason,
          editedBy: await getItem("userId"),
          editedAt: new Date()
        }
      };

      console.log("Update data being sent:", JSON.stringify(updateData, null, 2));

      const response = await makeRequest2(
        `${update_lease}/${facilityId}/${actualLeaseId}`,
        "PUT",
        updateData
      );

      if (response.success) {
        toastify("Lease updated successfully!", "success");
        if (onUpdate) onUpdate();
        onHide();
      } else {
        toastify(response.error || "Failed to update lease", "error");
      }
    } catch (error) {
      console.error('Error updating lease:', error);
      toastify("Error updating lease", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (activeIndex < 4) setActiveIndex(activeIndex + 1);
  };

  const handlePrevious = () => {
    if (activeIndex > 0) setActiveIndex(activeIndex - 1);
  };

  return (
    <Dialog
      visible={visible}
      style={{ width: "95vw", maxWidth: "1200px", height: "90vh" }}
      header={
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center">
            <i className="ti ti-edit me-2"></i>
            Edit Lease Agreement
          </div>
          <div className="bg-primary-50 text-primary-600 px-3 py-2 border-round font-semibold">
            Step {activeIndex + 1} of 5
          </div>
        </div>
      }
      modal
      onHide={onHide}
      footer={
        <div className="d-flex justify-content-between">
          <Button
            label="Cancel"
            icon="ti ti-times"
            className="p-button-text"
            onClick={onHide}
            disabled={submitting}
          />
          <div className="d-flex gap-2">
            {activeIndex > 0 && (
              <Button
                label="Previous"
                icon="ti ti-arrow-left"
                className="p-button-outlined"
                onClick={handlePrevious}
                disabled={submitting}
              />
            )}
            {activeIndex < 4 ? (
              <Button
                label="Next"
                icon="ti ti-arrow-right"
                onClick={handleNext}
                disabled={submitting}
              />
            ) : (
              <Button
                label="Update Lease"
                icon="ti ti-check"
                className="p-button-success"
                onClick={handleSubmit}
                loading={submitting}
                disabled={!editReason.trim()}
              />
            )}
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="text-center p-4">
          <i className="pi pi-spin pi-spinner text-primary" style={{ fontSize: '2rem' }}></i>
          <p className="mt-3">Loading lease data...</p>
        </div>
      ) : (
        <>
          {/* Edit Reason */}
          <div className="mb-4 p-3 border border-warning rounded bg-warning-light">
            <label className="fw-bold text-warning mb-2 d-block">
              <i className="ti ti-edit me-2"></i>
              Reason for Editing (Required)
            </label>
            <InputTextarea
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              placeholder="Please provide a reason for editing this lease agreement..."
              rows={2}
              className="w-100"
            />
          </div>

          <TabView
            activeIndex={activeIndex}
            onTabChange={(e) => setActiveIndex(e.index)}
            className="lease-edit-tabs"
          >
            {/* Tab 1: Basic Information */}
            <TabPanel header={
              <div className="d-flex align-items-center gap-2">
                <i className="ti ti-info-circle"></i>
                <span>Basic Information</span>
              </div>
            }>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Unit *</label>
                  <Dropdown
                    value={formData.unitId}
                    options={units}
                    optionLabel="name"
                    optionValue="_id"
                    onChange={(e) => {
                      const selectedUnit = units.find(u => u._id === e.value);
                      setFormData(prev => ({
                        ...prev,
                        unitId: e.value,
                        unitName: selectedUnit?.name || "",
                        tenantId: selectedUnit?.tenantId || "",
                        tenantName: selectedUnit?.tenantId ?
                          tenants.find(t => t._id === selectedUnit.tenantId)?.firstName + " " +
                          tenants.find(t => t._id === selectedUnit.tenantId)?.lastName : "",
                        landlordId: selectedUnit?.homeOwnerId || "",
                        landlordName: selectedUnit?.homeOwnerId ?
                          landlords.find(l => l._id === selectedUnit.homeOwnerId)?.firstName + " " +
                          landlords.find(l => l._id === selectedUnit.homeOwnerId)?.lastName : ""
                      }));
                    }}
                    placeholder="Select unit"
                    className="w-100"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label>Tenant</label>
                  <div className="form-control bg-light" style={{ height: "42px", lineHeight: "26px" }}>
                    {formData.tenantName || "Will be auto-filled when unit is selected"}
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <label>Landlord</label>
                  <div className="form-control bg-light" style={{ height: "42px", lineHeight: "26px" }}>
                    {formData.landlordName || "Will be auto-filled when unit is selected"}
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <label>Currency *</label>
                  <Select
                    value={formData.currency}
                    onChange={(selected) => setFormData(prev => ({ ...prev, currency: selected }))}
                    options={currencies.map(curr => ({
                      value: curr._id,
                      label: `${curr.currencyName} (${curr.currencyShortCode})`
                    }))}
                    placeholder="Select currency"
                    isClearable
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label>Status</label>
                  <Dropdown
                    value={formData.status}
                    options={[
                      { label: "Active", value: "Active" },
                      { label: "Inactive", value: "Inactive" },
                      { label: "Terminated", value: "Terminated" }
                    ]}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.value }))}
                    className="w-100"
                  />
                </div>
              </div>
            </TabPanel>

            {/* Tab 2: Lease Terms */}
            <TabPanel header={
              <div className="d-flex align-items-center gap-2">
                <i className="ti ti-calendar"></i>
                <span>Lease Terms</span>
              </div>
            }>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Start Date *</label>
                  <Calendar
                    value={formData.startDate}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, startDate: e.value }));
                      if (e.value && formData.endDate) {
                        const months = Math.round((formData.endDate - e.value) / (1000 * 60 * 60 * 24 * 30.44));
                        setFormData(prev => ({ ...prev, duration: months.toString() }));
                      }
                    }}
                    showIcon
                    className="w-100"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label>End Date *</label>
                  <Calendar
                    value={formData.endDate}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, endDate: e.value }));
                      if (e.value && formData.startDate) {
                        const months = Math.round((e.value - formData.startDate) / (1000 * 60 * 60 * 24 * 30.44));
                        setFormData(prev => ({ ...prev, duration: months.toString() }));
                      }
                    }}
                    showIcon
                    className="w-100"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label>Duration (Months)</label>
                  <InputText
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                    placeholder="Duration in months"
                    className="w-100"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label>Lease Template</label>
                  <Dropdown
                    value={formData.leaseTemplate}
                    options={templates}
                    optionLabel="name"
                    optionValue="_id"
                    onChange={(e) => setFormData(prev => ({ ...prev, leaseTemplate: e.value }))}
                    placeholder="Select template"
                    className="w-100"
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <div className="d-flex align-items-center">
                    <Checkbox
                      checked={formData.autoRenewal}
                      onChange={(e) => setFormData(prev => ({ ...prev, autoRenewal: e.checked }))}
                    />
                    <label className="ms-2">Auto Renewal</label>
                  </div>
                </div>
              </div>
            </TabPanel>

            {/* Tab 3: Financial Terms */}
            <TabPanel header={
              <div className="d-flex align-items-center gap-2">
                <i className="ti ti-currency-dollar"></i>
                <span>Financial Terms</span>
              </div>
            }>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Monthly Rent *</label>
                  <InputText
                    value={formData.monthlyRent}
                    onChange={(e) => setFormData(prev => ({ ...prev, monthlyRent: e.target.value }))}
                    placeholder="Monthly rent amount"
                    className="w-100"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label>Security Deposit *</label>
                  <InputText
                    value={formData.securityDeposit}
                    onChange={(e) => setFormData(prev => ({ ...prev, securityDeposit: e.target.value }))}
                    placeholder="Security deposit amount"
                    className="w-100"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label>Payment Due Date *</label>
                  <Dropdown
                    value={formData.paymentDueDate}
                    options={Array.from({ length: 31 }, (_, i) => ({
                      label: `${i + 1} of each month`,
                      value: (i + 1).toString()
                    }))}
                    onChange={(e) => setFormData(prev => ({ ...prev, paymentDueDate: e.value }))}
                    placeholder="Select due date"
                    className="w-100"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label>Balance Brought Forward</label>
                  <InputText
                    value={formData.balanceBroughtForward}
                    onChange={(e) => setFormData(prev => ({ ...prev, balanceBroughtForward: e.target.value }))}
                    placeholder="Outstanding balance"
                    className="w-100"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label>Billing Frequency</label>
                  <Dropdown
                    value={formData.billingFrequency}
                    options={[
                      { label: "Monthly", value: "Monthly" },
                      { label: "Quarterly", value: "Quarterly" },
                      { label: "Yearly", value: "Yearly" }
                    ]}
                    onChange={(e) => setFormData(prev => ({ ...prev, billingFrequency: e.value }))}
                    className="w-100"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label>Next Invoice Date</label>
                  <Calendar
                    value={formData.nextInvoiceDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, nextInvoiceDate: e.value }))}
                    showIcon
                    className="w-100"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    DigiTax Submission Profile
                  </label>

                  <select
                    className="form-control"
                    value={formData.digitaxSelection}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === "") {
                        setFormData(prev => ({
                          ...prev,
                          digitaxEnabled: false,
                          digitaxProfileId: null,
                          digitaxSelection: ""
                        }));
                      } else if (value === "legacy") {
                        setFormData(prev => ({
                          ...prev,
                          digitaxEnabled: true,
                          digitaxProfileId: null,
                          digitaxSelection: "legacy"
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          digitaxEnabled: true,
                          digitaxProfileId: value,
                          digitaxSelection: value
                        }));
                      }
                    }}
                  >
                    <option value="">
                      Do not submit to DigiTax
                    </option>

                    <option value="legacy">
                      Use Facility DigiTax Configuration
                    </option>

                    {digitaxProfiles
                      .filter(profile => profile.enabled)
                      .map(profile => (
                        <option
                          key={profile._id}
                          value={profile._id}
                        >
                          {profile.name}
                        </option>
                      ))}
                  </select>
                </div>

              </div>
            </TabPanel>

            {/* Tab 4: Payment Methods */}
            <TabPanel header={
              <div className="d-flex align-items-center gap-2">
                <i className="ti ti-credit-card"></i>
                <span>Payment Methods</span>
              </div>
            }>
              <div className="row">
                <div className="col-12 mb-4">
                  <h6 className="text-900 font-semibold mb-3">
                    <i className="ti ti-credit-card me-2"></i>
                    Payment Methods
                  </h6>
                  {formData.paymentMethods.map((method, index) => {
                    return (
                      <div key={index} className="card mb-3 p-3 border">
                        <div className="row align-items-center">
                          <div className="col-md-3 mb-2">
                            <label className="form-label">Payment Type *</label>
                            <Dropdown
                              value={method.type}
                              options={[
                                { label: "Cash", value: "Cash" },
                                { label: "Bank Transfer", value: "Bank Transfer" },
                                { label: "Mobile Money", value: "Mobile Money" },
                                { label: "Cheque", value: "Cheque" }
                              ]}
                              onChange={(e) => {
                                const updatedMethods = [...formData.paymentMethods];
                                updatedMethods[index] = {
                                  ...updatedMethods[index],
                                  type: e.value,
                                  details: {}
                                };
                                setFormData(prev => ({ ...prev, paymentMethods: updatedMethods }));
                              }}
                              placeholder="Select payment type"
                              className="w-100"
                            />
                          </div>

                          {method.type === "Cash" && (
                            <div className="col-md-6 mb-2">
                              <label className="form-label">Preferred Cash Location</label>
                              <InputText
                                value={method.details.preferredCashLocation || ""}
                                onChange={(e) => {
                                  const updatedMethods = [...formData.paymentMethods];
                                  updatedMethods[index].details.preferredCashLocation = e.target.value;
                                  setFormData(prev => ({ ...prev, paymentMethods: updatedMethods }));
                                }}
                                placeholder="Office, Security desk, etc."
                                className="w-100"
                              />
                            </div>
                          )}

                          {method.type === "Bank Transfer" && (
                            <>
                              {(method.bankDetailsId || (method.details && (method.details.bankName || method.details.accountName))) && (() => {
                                if (method.bankDetailsId) {
                                  const selectedBank = bankDetails.find(b => b._id === method.bankDetailsId);
                                  return selectedBank ? (
                                    <div className="col-12 mb-2">
                                      <div className="alert alert-info d-flex justify-content-between align-items-center">
                                        <div>
                                          <strong>Selected Bank:</strong> {selectedBank.bankName} - {selectedBank.accountNumber}
                                          <div className="small text-muted">Account: {selectedBank.accountName}</div>
                                          {selectedBank.branchName && <div className="small text-muted">Branch: {selectedBank.branchName}</div>}
                                        </div>
                                        <small className="text-muted">Change below if needed</small>
                                      </div>
                                    </div>
                                  ) : null;
                                } else if (method.details && (method.details.bankName || method.details.accountName)) {
                                  return (
                                    <div className="col-12 mb-2">
                                      <div className="alert alert-warning d-flex justify-content-between align-items-center">
                                        <div>
                                          <strong>Current Bank Details:</strong>
                                          {method.details.bankName && <div>Bank: {method.details.bankName}</div>}
                                          {method.details.accountName && <div>Account: {method.details.accountName}</div>}
                                          {method.details.accountNumber && <div>Account Number: {method.details.accountNumber}</div>}
                                          {method.details.branch && <div className="small text-muted">Branch: {method.details.branch}</div>}
                                        </div>
                                        <small className="text-muted">Manual entry</small>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}

                              <div className="col-md-3 mb-2">
                                <label className="form-label">
                                  {method.bankDetailsId ? 'Change Bank Details' : 'Bank Details'}
                                </label>
                                <Dropdown
                                  value={method.bankDetailsId || ""}
                                  options={[
                                    { label: "Select saved bank", value: "" },
                                    ...bankDetails.map(bank => ({
                                      label: `${bank.bankName} (${bank.accountName}) - ${bank.accountNumber}`,
                                      value: bank._id
                                    }))
                                  ]}
                                  onChange={(e) => {
                                    const updatedMethods = [...formData.paymentMethods];
                                    updatedMethods[index].bankDetailsId = e.value || null;
                                    if (e.value && e.value !== "") {
                                      const selectedBank = bankDetails.find(b => b._id === e.value);
                                      if (selectedBank) {
                                        updatedMethods[index].details = {
                                          bankName: selectedBank.bankName,
                                          accountNumber: selectedBank.accountNumber
                                        };
                                      }
                                    } else {
                                      updatedMethods[index].bankDetailsId = null;
                                      updatedMethods[index].details = {
                                        bankName: "",
                                        accountNumber: ""
                                      };
                                    }
                                    setFormData(prev => ({ ...prev, paymentMethods: updatedMethods }));
                                  }}
                                  placeholder="Select bank"
                                  className="w-100"
                                />
                              </div>
                              <div className="col-md-2 mb-2">
                                <label className="form-label">Bank Name *</label>
                                <InputText
                                  value={method.details.bankName || ""}
                                  onChange={(e) => {
                                    const updatedMethods = [...formData.paymentMethods];
                                    updatedMethods[index].details.bankName = e.target.value;
                                    setFormData(prev => ({ ...prev, paymentMethods: updatedMethods }));
                                  }}
                                  placeholder="Bank name"
                                  className="w-100"
                                  disabled={!!method.bankDetailsId}
                                />
                              </div>
                              <div className="col-md-2 mb-2">
                                <label className="form-label">Account Number *</label>
                                <InputText
                                  value={method.details.accountNumber || ""}
                                  onChange={(e) => {
                                    const updatedMethods = [...formData.paymentMethods];
                                    updatedMethods[index].details.accountNumber = e.target.value;
                                    setFormData(prev => ({ ...prev, paymentMethods: updatedMethods }));
                                  }}
                                  placeholder="Account number"
                                  className="w-100"
                                  disabled={!!method.bankDetailsId}
                                />
                              </div>
                            </>
                          )}

                          {method.type === "Mobile Money" && (
                            <div className="col-md-6 mb-2">
                              <label className="form-label">Payment Method <span className="text-danger">*</span></label>
                              <Dropdown
                                value={method.details?.businessNumber || ""}
                                options={[
                                  { label: "All - 4930842", value: "4930842" }
                                ]}
                                onChange={(e) => {
                                  const updatedMethods = [...formData.paymentMethods];
                                  updatedMethods[index].details = { businessNumber: e.value };
                                  setFormData(prev => ({ ...prev, paymentMethods: updatedMethods }));
                                }}
                                placeholder="Select payment method"
                                className="w-100"
                              />
                              <small className="text-muted">Select mobile money payment method</small>
                            </div>
                          )}

                          {method.type === "Cheque" && (
                            <div className="col-md-6 mb-2">
                              <label className="form-label">Bank to Draft Cheque *</label>
                              <InputText
                                value={method.details.bankToDraft || ""}
                                onChange={(e) => {
                                  const updatedMethods = [...formData.paymentMethods];
                                  updatedMethods[index].details.bankToDraft = e.target.value;
                                  setFormData(prev => ({ ...prev, paymentMethods: updatedMethods }));
                                }}
                                placeholder="Bank name for cheque"
                                className="w-100"
                              />
                            </div>
                          )}

                          <div className="col-md-2 mb-2 d-flex align-items-center">
                            <Checkbox
                              checked={method.isPrimary}
                              onChange={(e) => {
                                const updatedMethods = formData.paymentMethods.map((m, i) => ({
                                  ...m,
                                  isPrimary: i === index ? e.checked : false
                                }));
                                setFormData(prev => ({ ...prev, paymentMethods: updatedMethods }));
                              }}
                            />
                            <label className="ms-2">Primary</label>
                          </div>

                          <div className="col-md-1 mb-2 d-flex justify-content-end">
                            {formData.paymentMethods.length > 1 && (
                              <Button
                                icon="ti ti-trash"
                                className="p-button-danger p-button-text"
                                onClick={() => {
                                  const updatedMethods = formData.paymentMethods.filter((_, i) => i !== index);
                                  setFormData(prev => ({ ...prev, paymentMethods: updatedMethods }));
                                }}
                                tooltip="Remove payment method"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <Button
                    label="Add Payment Method"
                    icon="ti ti-plus"
                    className="p-button-outlined"
                    onClick={() => {
                      const newMethod = { type: "", details: {}, isPrimary: false };
                      setFormData(prev => ({
                        ...prev,
                        paymentMethods: [...prev.paymentMethods, newMethod]
                      }));
                    }}
                  />
                </div>
              </div>
            </TabPanel>

            {/* Tab 5: GL Accounts */}
            <TabPanel header={
              <div className="d-flex align-items-center gap-2">
                <i className="ti ti-calculator"></i>
                <span>GL Accounts</span>
              </div>
            }>
              <div className="row">
                <div className="col-12 mb-4">
                  <div className="surface-card p-4 border-round-lg shadow-1">
                    <div className="flex align-items-center mb-3">
                      <i className="ti ti-calculator text-2xl text-primary mr-3"></i>
                      <h5 className="text-900 font-semibold m-0">GL Accounts Configuration</h5>
                    </div>
                    <p className="text-600 mb-4">
                      Configure the General Ledger accounts for invoice and payment processing.
                    </p>

                    <div className="row">
                      {/* Invoice Accounts */}
                      <div className="col-12 mb-4">
                        <h6 className="text-900 font-semibold mb-3">
                          <i className="ti ti-file-invoice me-2"></i>
                          Invoice Accounts
                        </h6>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Invoice Debit Account <span className="text-danger">*</span>
                            </label>
                            <Dropdown
                              value={formData.glAccounts.invoice.debit}
                              options={glAccountsList.map(account => ({
                                label: `${account.accountCode} - ${account.accountName}`,
                                value: account._id
                              }))}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                glAccounts: {
                                  ...prev.glAccounts,
                                  invoice: {
                                    ...prev.glAccounts.invoice,
                                    debit: e.value
                                  }
                                }
                              }))}
                              placeholder="Select debit account for invoices"
                              className="w-100"
                              filter
                              showClear
                            />
                            <small className="text-muted">
                              Account to be debited when creating invoices (typically Accounts Receivable)
                            </small>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Invoice Credit Account <span className="text-danger">*</span>
                            </label>
                            <Dropdown
                              value={formData.glAccounts.invoice.credit}
                              options={glAccountsList.map(account => ({
                                label: `${account.accountCode} - ${account.accountName}`,
                                value: account._id
                              }))}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                glAccounts: {
                                  ...prev.glAccounts,
                                  invoice: {
                                    ...prev.glAccounts.invoice,
                                    credit: e.value
                                  }
                                }
                              }))}
                              placeholder="Select credit account for invoices"
                              className="w-100"
                              filter
                              showClear
                            />
                            <small className="text-muted">
                              Account to be credited when creating invoices (typically Revenue)
                            </small>
                          </div>
                        </div>
                      </div>

                      {/* Payment Accounts */}
                      <div className="col-12 mb-4">
                        <h6 className="text-900 font-semibold mb-3">
                          <i className="ti ti-credit-card me-2"></i>
                          Payment Accounts
                        </h6>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Payment Debit Account <span className="text-danger">*</span>
                            </label>
                            <Dropdown
                              value={formData.glAccounts.payment.debit}
                              options={glAccountsList.map(account => ({
                                label: `${account.accountCode} - ${account.accountName}`,
                                value: account._id
                              }))}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                glAccounts: {
                                  ...prev.glAccounts,
                                  payment: {
                                    ...prev.glAccounts.payment,
                                    debit: e.value
                                  }
                                }
                              }))}
                              placeholder="Select debit account for payments"
                              className="w-100"
                              filter
                              showClear
                            />
                            <small className="text-muted">
                              Account to be debited when receiving payments (typically Cash/Bank)
                            </small>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Payment Credit Account <span className="text-danger">*</span>
                            </label>
                            <Dropdown
                              value={formData.glAccounts.payment.credit}
                              options={glAccountsList.map(account => ({
                                label: `${account.accountCode} - ${account.accountName}`,
                                value: account._id
                              }))}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                glAccounts: {
                                  ...prev.glAccounts,
                                  payment: {
                                    ...prev.glAccounts.payment,
                                    credit: e.value
                                  }
                                }
                              }))}
                              placeholder="Select credit account for payments"
                              className="w-100"
                              filter
                              showClear
                            />
                            <small className="text-muted">
                              Account to be credited when receiving payments (typically Accounts Receivable)
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabPanel>
          </TabView>
        </>
      )}

      <style jsx>{`
        .lease-edit-tabs .p-tabview-nav li .p-tabview-nav-link {
          padding: 1rem 1.5rem;
          font-weight: 600;
          border-radius: 0.5rem 0.5rem 0 0;
          margin-right: 0.25rem;
        }
        .lease-edit-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link {
          background: var(--primary-color);
          color: white;
        }
      `}</style>
    </Dialog>
  );
};

export default EditLeaseDialog;